import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Input, Row, Space, Table, message } from 'antd';
import { useTranslation } from 'react-i18next';
import EditableDrawer from '../../../../components/EditableDrawer';
import TablePaginationBar from '../../../../components/TablePaginationBar';
import { gql, useLazyQuery, useQuery } from '@apollo/client';
import CentralParcelDetailDrawer from '../../../central/parcels/components/CentralParcelDetailDrawer';
import { getUserInfo } from '../../../../utils/auth';

export type UndeliverableUploadTaskRow = {
  key: string;
  taskNo: string;
  displayFileName: string;
  fileObjectKey: string;
  createdBy?: string | null;
  legacyGroup: boolean;
};

interface Props {
  open: boolean;
  onClose: () => void;
  task: UndeliverableUploadTaskRow | null;
}

const GET_ORDER_BY_TRACKING_NUMBER = gql`
  query GetOrderByTrackingNumber($trackingNumber: String!) {
    getOrderByTrackingNumber(trackingNumber: $trackingNumber) {
      id
      trackingNumber
    }
  }
`;

const LIST_UNDELIVERABLE_UPLOAD_TASK_TRACKING_NUMBERS = gql`
  query ListUndeliverableUploadTaskTrackingNumbers(
    $taskNo: String
    $legacyFileObjectKey: String
    $legacyCreatedBy: String
    $keyword: String
    $page: Int!
    $size: Int!
  ) {
    getUndeliverableReasonDictionaries {
      reasonCode
      labelZh
      labelEn
      labelEs
    }
    listUndeliverableUploadTaskTrackingNumbers(
      taskNo: $taskNo
      legacyFileObjectKey: $legacyFileObjectKey
      legacyCreatedBy: $legacyCreatedBy
      keyword: $keyword
      page: $page
      size: $size
    ) {
      totalElements
      content {
        trackingNumber
        reasonCode
      }
    }
  }
`;

const UndeliverableUploadTaskDrawer: React.FC<Props> = ({ open, onClose, task }) => {
  const { t, i18n } = useTranslation();
  const [keywordInput, setKeywordInput] = useState('');
  const [appliedKeyword, setAppliedKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [parcelDetailVisible, setParcelDetailVisible] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string>('');

  const [loadOrderByTracking, { loading: lookupLoading }] = useLazyQuery(GET_ORDER_BY_TRACKING_NUMBER, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (open) {
      setKeywordInput('');
      setAppliedKeyword('');
      setPage(1);
      setSelectedRowKeys([]);
    }
  }, [open, task?.key]);

  const keywordForQuery = useMemo(() => appliedKeyword.trim() || null, [appliedKeyword]);

  const trackingQueryVariables = useMemo(() => {
    if (!task) {
      return null;
    }
    if (task.legacyGroup) {
      return {
        taskNo: null as string | null,
        legacyFileObjectKey: task.fileObjectKey,
        legacyCreatedBy: task.createdBy ?? null,
        keyword: keywordForQuery,
        page,
        size: pageSize,
      };
    }
    const tn = (task.taskNo || '').trim();
    return {
      taskNo: tn || null,
      legacyFileObjectKey: null as string | null,
      legacyCreatedBy: null as string | null,
      keyword: keywordForQuery,
      page,
      size: pageSize,
    };
  }, [task, keywordForQuery, page, pageSize]);

  const canQueryTracking = Boolean(
    open &&
      task &&
      trackingQueryVariables &&
      (task.legacyGroup
        ? Boolean(task.fileObjectKey?.trim())
        : Boolean((task.taskNo || '').trim()) && (task.taskNo || '').trim() !== '-')
  );

  const trackingPageQuery = useQuery(LIST_UNDELIVERABLE_UPLOAD_TASK_TRACKING_NUMBERS, {
    variables: trackingQueryVariables ?? {
      taskNo: null,
      legacyFileObjectKey: null,
      legacyCreatedBy: null,
      keyword: null,
      page: 1,
      size: 10,
    },
    skip: !canQueryTracking,
    fetchPolicy: 'network-only',
  });

  const trackingPage = trackingPageQuery.data?.listUndeliverableUploadTaskTrackingNumbers;
  const totalElements: number = Number(trackingPage?.totalElements ?? 0) || 0;
  const pagedTrackingItems: { trackingNumber: string; reasonCode: string }[] = (trackingPage?.content ?? [])
    .map((item: any) => ({
      trackingNumber: String(item?.trackingNumber ?? ''),
      reasonCode: String(item?.reasonCode ?? ''),
    }))
    .filter((item: { trackingNumber: string }) => Boolean(item.trackingNumber));
  const reasonDictionaries = trackingPageQuery.data?.getUndeliverableReasonDictionaries ?? [];
  const reasonLabelMap = useMemo<Map<string, string>>(() => {
    const lang = (i18n.language || '').toLowerCase();
    const labelKey = lang.startsWith('zh') ? 'labelZh' : lang.startsWith('es') ? 'labelEs' : 'labelEn';
    return new Map<string, string>(
      reasonDictionaries.map((item: any) => [
        String(item.reasonCode),
        String(item[labelKey] || item.labelEn || item.labelZh || item.reasonCode),
      ])
    );
  }, [i18n.language, reasonDictionaries]);

  const copyTrackingNumbers = async () => {
    const picked =
      selectedRowKeys.length > 0
        ? selectedRowKeys.map((k) => String(k)).filter(Boolean)
        : pagedTrackingItems.map((item) => item.trackingNumber);
    const text = picked.join('\n');
    if (!text) {
      message.error(t('qualityControl.undeliverableUpload.drawer.copyEmpty'));
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      message.success(t('qualityControl.undeliverableUpload.drawer.copySucceeded', { count: picked.length }));
    } catch (e) {
      message.error(t('qualityControl.undeliverableUpload.drawer.copyFailed'));
    }
  };

  const applyFilter = () => {
    setAppliedKeyword(keywordInput);
    setPage(1);
    setSelectedRowKeys([]);
  };

  const openParcelDetailByTrackingNumber = async (trackingNumber: string) => {
    const tn = (trackingNumber || '').trim();
    if (!tn) return;
    try {
      const res = await loadOrderByTracking({ variables: { trackingNumber: tn } });
      const id = res.data?.getOrderByTrackingNumber?.id;
      if (!id) {
        message.error(t('qualityControl.undeliverableUpload.drawer.orderNotFound', { trackingNumber: tn }));
        return;
      }
      setSelectedOrderId(String(id));
      setParcelDetailVisible(true);
    } catch (e: any) {
      message.error(e?.message || t('qualityControl.undeliverableUpload.drawer.orderLookupFailed'));
    }
  };

  const drawerTitle = (
    <span className="text-lg font-semibold border-l-4 border-purple-600 pl-2" style={{ whiteSpace: 'pre-line' }}>
      {t('qualityControl.undeliverableUpload.drawer.title', { taskNo: task?.taskNo || '-' })}
    </span>
  );

  const tnRows = useMemo(
    () =>
      pagedTrackingItems.map((item) => ({
        id: item.trackingNumber,
        trackingNumber: item.trackingNumber,
        reasonCode: item.reasonCode,
      })),
    [pagedTrackingItems]
  );

  const columns = useMemo(
    () => [
      {
        title: t('qualityControl.undeliverableUpload.drawer.table.trackingNumber'),
        dataIndex: 'trackingNumber',
        key: 'trackingNumber',
        width: '58%',
        render: (v: string) => {
          const tn = (v || '').trim();
          if (!tn) return <span>-</span>;
          return (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                void openParcelDetailByTrackingNumber(tn);
              }}
              style={{ color: '#1890ff' }}
            >
              {tn}
            </a>
          );
        },
      },
      {
        title: t('qualityControl.undeliverableUpload.drawer.table.reason'),
        dataIndex: 'reasonCode',
        key: 'reasonCode',
        width: '42%',
        render: (v: string) => {
          const text = reasonLabelMap.get(v) || v || '-';
          return <span>{text}</span>;
        },
      },
    ],
    [openParcelDetailByTrackingNumber, reasonLabelMap, t]
  );

  return (
    <EditableDrawer title={drawerTitle} visible={open} onClose={onClose} width={600} showDefaultFooter={false} destroyOnClose>
      <Row gutter={[16, 8]} className="mb-2" justify="space-between" align="middle">
        <Col flex="auto">
          <Input
            allowClear
            value={keywordInput}
            onChange={(e) => {
              const v = e.target.value;
              setKeywordInput(v);
              // Clearing the input immediately restores the full result set.
              if (!v) {
                setAppliedKeyword('');
                setPage(1);
                setSelectedRowKeys([]);
              }
            }}
            onPressEnter={applyFilter}
            placeholder={String(t('qualityControl.undeliverableUpload.drawer.searchPlaceholder'))}
            style={{ minWidth: 200, maxWidth: 280 }}
          />
        </Col>
        <Col>
          <Space>
            {selectedRowKeys.length > 0 && (
              <span className="text-gray-500 text-sm">
                {t('qualityControl.undeliverableUpload.drawer.selectedCount', { count: selectedRowKeys.length })}
              </span>
            )}
            <Button type="primary" className="w-auto min-w-24" onClick={applyFilter}>
              <span className="font-bold">{t('qualityControl.undeliverableUpload.drawer.filterButton')}</span>
            </Button>
            <Button type="primary" className="w-auto min-w-24" onClick={() => void copyTrackingNumbers()}>
              <span className="font-bold">{t('qualityControl.undeliverableUpload.drawer.copyButton')}</span>
            </Button>
          </Space>
        </Col>
      </Row>

      <Table
        dataSource={tnRows}
        columns={columns as any}
        rowKey="id"
        pagination={false}
        size="small"
        tableLayout="fixed"
        style={{ width: '100%' }}
        locale={{ emptyText: t('qualityControl.undeliverableUpload.drawer.empty') as any }}
        loading={trackingPageQuery.loading}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
        }}
      />

      <TablePaginationBar
        total={totalElements}
        current={page}
        pageSize={pageSize}
        onChange={(p, ps) => {
          setPage(p);
          setPageSize(ps);
        }}
      />

      <CentralParcelDetailDrawer
        visible={parcelDetailVisible}
        orderId={selectedOrderId}
        userRole={getUserInfo()?.account?.role?.name ?? ''}
        onClose={() => setParcelDetailVisible(false)}
      />
    </EditableDrawer>
  );
};

export default UndeliverableUploadTaskDrawer;

